// Market Forge — GEX Panel (Gamma Exposure regime filter).
// Self-contained: fetches /api/forge/gex on open and every 60s while open,
// renders the regime/classification/score header, an SVG horizontal-bar
// gamma profile chart with an overlaid "Net GEX vs Price" curve, reference
// lines, a stats grid, sign/scale toggles, scorer notes (locale-aware),
// a Phase 6 sparkline of zero-gamma drift vs spot, and a fullscreen
// modal so the user can blow the chart up for analysis.
//
// Per the user's strategy: GEX never overrides ROI/WES. It modulates the
// classification only. The `onUpdate(report)` callback bubbles the report
// up to MarketForge so the Decision panel can show a classification chip.

import React, { useEffect, useMemo, useState, useRef } from "react";
import axios from "axios";
import { ChevronDown, ChevronRight, Maximize2, X } from "lucide-react";
import { API, GEX_CLASS_COLORS } from "./utils";
import { StrategyTracker } from "./StrategyTracker";
import GEXStrikePathCard from "./GEXStrikePathCard";

const REGIME_COLORS = {
  long_gamma:  "#00FF88",
  short_gamma: "#FF3366",
  transition:  "#FFB800",
  unknown:     "#8A8A93",
};

// Iter119.25 — Re-export for back-compat: prior to extracting this to
// utils.js, the canonical home for GEX_CLASS_COLORS was this file. Any
// external consumer still importing from `./GEXPanel` keeps working.
export { GEX_CLASS_COLORS };

// Render a backend scorer note (structured `{code, args}`) through i18n.
// Falls back to the legacy English text if the code isn't in the locale.
function renderNote(t, n) {
  if (typeof n === "string") return n;  // back-compat — older payloads
  if (!n || !n.code) return "";
  const tpl = t(`gex.note.${n.code}`, n.code);
  if (!tpl || !n.args) return tpl;
  return Object.entries(n.args).reduce(
    (acc, [k, v]) => acc.replaceAll(`\${${k}}`, String(v ?? "—")),
    tpl,
  );
}

export function GEXPanel({ symbol, timeframe, candidateStrike, t, onUpdate }) {
  const [open, setOpen] = useState(true);
  const [fullscreen, setFullscreen] = useState(false);
  const [signModel, setSignModel] = useState(() => localStorage.getItem("forge.gex.sign") || "dealer_short");
  const [scaleMode, setScaleMode] = useState(() => localStorage.getItem("forge.gex.scale") || "percent_1");
  const [data, setData] = useState(null);
  const [history, setHistory] = useState(null);
  const [stability, setStability] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // Manual candidate strike override — when the user drags the yellow line
  // the GEX panel re-queries the API with this strike so the scorer & notes
  // update live. `null` means "use the auto-picked candidate strike".
  const [manualStrike, setManualStrike] = useState(null);
  const effectiveStrike = manualStrike != null ? manualStrike : candidateStrike;
  // Iter29 — Wheel ITM cycle objective. Stored in localStorage per symbol so
  // the user's choice persists across reloads. `null` defers to the
  // backend's auto-inference (CC_CYCLE_CLOSURE if shares assigned, etc.).
  const [objective, setObjective] = useState(() => localStorage.getItem("forge.gex.objective") || null);
  const [showAdvanced, setShowAdvanced] = useState(() => localStorage.getItem("forge.gex.advanced") === "1");

  useEffect(() => { if (objective) localStorage.setItem("forge.gex.objective", objective); }, [objective]);
  useEffect(() => { localStorage.setItem("forge.gex.advanced", showAdvanced ? "1" : "0"); }, [showAdvanced]);

  useEffect(() => { localStorage.setItem("forge.gex.sign", signModel); }, [signModel]);
  useEffect(() => { localStorage.setItem("forge.gex.scale", scaleMode); }, [scaleMode]);
  // Only reset the manual override when the user switches symbols or
  // timeframes. The parent's `candidateStrike` prop can change on every scan
  // tick — resetting on that would kick the user out of their drag override.
  useEffect(() => { setManualStrike(null); }, [symbol, timeframe]);

  useEffect(() => {
    if (data && typeof onUpdate === "function") onUpdate(data);
  }, [data, onUpdate]);

  // Esc closes the fullscreen overlay
  useEffect(() => {
    if (!fullscreen) return;
    const onKey = (e) => { if (e.key === "Escape") setFullscreen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [fullscreen]);

  // Iter117.8 — Anti-flicker: only nuke stale data when SYMBOL changes.
  // The previous behaviour cleared data on every effect run, which meant
  // every 60-second polling cycle blanked the panel for ~1s mid-refresh.
  const gexDataSymbolRef = useRef(null);
  useEffect(() => {
    if (!open || !symbol) return;
    const symbolChanged = symbol !== gexDataSymbolRef.current;
    if (symbolChanged) {
      setData(null);
      setHistory(null);
      setStability(null);
      setError(null);
    }
    let alive = true;
    const fetchGex = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams({
          symbol,
          timeframe: timeframe || "1h",
          sign_model: signModel,
          scale_mode: scaleMode,
        });
        if (effectiveStrike) params.set("candidate_strike", String(effectiveStrike));
        if (objective) params.set("objective", objective);
        const r = await axios.get(`${API}/forge/gex?${params.toString()}`);
        if (!alive) return;
        if (r.data && r.data.symbol && r.data.symbol.toUpperCase() !== String(symbol).toUpperCase()) {
          setError(`GEX DATA MISMATCH — server returned ${r.data.symbol} for ${symbol} request`);
          return;
        }
        setData(r.data);
        setError(null);
        gexDataSymbolRef.current = symbol;
      } catch (e) {
        if (alive) setError(e?.response?.data?.detail || t("gex.error"));
      } finally {
        if (alive) setLoading(false);
      }
    };
    const fetchHistory = async () => {
      try {
        const r = await axios.get(`${API}/forge/gex/history?symbol=${symbol}&days=7&sign_model=${signModel}`);
        if (!alive) return;
        // Reject mismatched history (the endpoint echoes `symbol`)
        if (r.data && r.data.symbol && r.data.symbol.toUpperCase() !== String(symbol).toUpperCase()) return;
        setHistory(r.data);
      } catch { /* best-effort */ }
    };
    const fetchStability = async () => {
      try {
        const r = await axios.get(`${API}/forge/gex/regime-stability?symbol=${symbol}&days=14&sign_model=${signModel}`);
        if (!alive) return;
        if (r.data && r.data.symbol && r.data.symbol.toUpperCase() !== String(symbol).toUpperCase()) return;
        setStability(r.data);
      } catch { /* best-effort */ }
    };
    fetchGex();
    fetchHistory();
    fetchStability();
    const id = setInterval(() => { fetchGex(); fetchHistory(); fetchStability(); }, 60000);
    return () => { alive = false; clearInterval(id); };
  }, [open, symbol, timeframe, signModel, scaleMode, effectiveStrike, objective, t]);

  const regime = data?.regime || "unknown";
  const klass = data?.classification || "NO_DATA";

  return (
    <>
    <div data-testid="forge-gex-panel" className="border-t border-[#26262B] bg-[#0E0E11]">
      <div className="flex items-center justify-between px-4 py-2 border-b border-[#26262B]">
        <button
          data-testid="forge-gex-toggle"
          onClick={() => setOpen(v => !v)}
          className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.18em] text-white hover:text-[#00FF88]"
        >
          {open ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
          {t("gex.title")}
          <span className="text-[9px] font-normal text-[#5F5F66] tracking-normal normal-case">· {t("gex.subtitle")}</span>
        </button>
        <div className="flex items-center gap-4">
          {data && (
            <>
              <span data-testid="gex-regime-badge"
                className="text-[10px] font-bold uppercase tracking-[0.15em] px-2 py-0.5 rounded-sm"
                style={{ color: REGIME_COLORS[regime], border: `1px solid ${REGIME_COLORS[regime]}55` }}>
                {t(`gex.regime.${regime}`)}
              </span>
              <span data-testid="gex-class-badge"
                className="text-[10px] font-bold uppercase tracking-[0.15em] px-2 py-0.5 rounded-sm"
                style={{ color: GEX_CLASS_COLORS[klass], border: `1px solid ${GEX_CLASS_COLORS[klass]}55` }}>
                {t(`gex.class.${klass}`)}
              </span>
              <span className="text-[10px] font-mono text-[#8A8A93]">
                {t("gex.score")}: <span className="text-white font-bold">{data.score}</span>
              </span>
              <button
                data-testid="forge-gex-fullscreen"
                onClick={() => setFullscreen(true)}
                title={t("gex.fullscreen")}
                className="text-[#8A8A93] hover:text-[#00FF88] transition-colors"
              >
                <Maximize2 size={14} />
              </button>
            </>
          )}
        </div>
      </div>

      {open && (
        <div className="p-4">
          {loading && !data && (
            <div data-testid="forge-gex-loading"
                 className="text-xs text-[#8A8A93] font-mono text-center py-8">
              {t("gex.loading")}
            </div>
          )}
          {error && !data && (
            <div data-testid="forge-gex-error"
                 className="text-xs text-[#FF3366] font-mono text-center py-8 px-4 leading-relaxed">
              {/* Iter35 — Explicit "no stale data" message. The panel state
                  is already wiped before each fetch (iter34), so when the
                  broker call fails we render a clear "no stale data shown"
                  notice rather than a bare error string. */}
              <div className="mb-1.5 font-bold uppercase tracking-[0.15em]">
                GEX unavailable for {symbol} — broker spot/chain fetch failed
              </div>
              <div className="text-[10px] text-[#8A8A93] mb-2">
                No stale data displayed.
              </div>
              <div className="text-[10px] text-[#8A8A93]">{error}</div>
            </div>
          )}
          {data && (
            <div className="grid grid-cols-12 gap-4">
              {/* Iter29 — primary surface: GEX Strike Path Card (Wheel ITM). */}
              <div className="col-span-12 lg:col-span-9 space-y-3">
                <GEXStrikePathCard
                  data={data}
                  objective={objective || data.strike_path?.objective}
                  onObjectiveChange={setObjective}
                  loading={loading}
                />
                {/* Advanced view: legacy curve / by-strike bars (toggleable). */}
                <div className="border-t border-[#26262B] pt-3">
                  <button
                    data-testid="gex-advanced-toggle"
                    onClick={() => setShowAdvanced(v => !v)}
                    className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#8A8A93] hover:text-white flex items-center gap-1.5"
                  >
                    {showAdvanced ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                    Advanced View · By-Strike Curve & Bars
                  </button>
                  {showAdvanced && (
                    <div className="mt-3 space-y-3">
                      <GEXProfileChart data={data} t={t} height={460}
                        manualStrike={manualStrike}
                        onStrikeChange={setManualStrike}
                        onStrikeReset={() => setManualStrike(null)}
                      />
                      <GEXHistorySparkline history={history} t={t} />
                    </div>
                  )}
                </div>
              </div>
              <div className="col-span-12 lg:col-span-3 flex flex-col gap-3 text-xs font-mono">
                <GEXStats data={data} t={t} />
                <GEXRegimeStability stability={stability} t={t} />
                <GEXControls
                  signModel={signModel} setSignModel={setSignModel}
                  scaleMode={scaleMode} setScaleMode={setScaleMode}
                  t={t}
                />
                <StrategyTracker symbol={symbol} signModel={signModel} t={t} />
                {data.notes?.length > 0 && (
                  <div data-testid="gex-notes" className="bg-[#121216] border border-[#26262B] rounded-sm p-2.5 space-y-1">
                    <div className="text-[9px] font-bold uppercase tracking-[0.15em] text-[#8A8A93]">{t("gex.warnings")}</div>
                    {data.notes.map((n, i) => (
                      <div key={i} className="text-[10px] text-[#E4E4E7] leading-snug">• {renderNote(t, n)}</div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>

    {fullscreen && data && (
      <div
        data-testid="forge-gex-fullscreen-overlay"
        className="fixed inset-0 z-[1000] bg-black/90 backdrop-blur-sm flex flex-col"
        onClick={() => setFullscreen(false)}
      >
        <div className="flex items-center justify-between px-6 py-3 border-b border-[#26262B] bg-[#0A0A0D]">
          <div className="flex items-center gap-3">
            <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-white">{t("gex.title")} · {symbol}</div>
            <span className="text-[10px] font-bold uppercase tracking-[0.15em] px-2 py-0.5 rounded-sm"
              style={{ color: REGIME_COLORS[regime], border: `1px solid ${REGIME_COLORS[regime]}55` }}>
              {t(`gex.regime.${regime}`)}
            </span>
            <span className="text-[10px] font-bold uppercase tracking-[0.15em] px-2 py-0.5 rounded-sm"
              style={{ color: GEX_CLASS_COLORS[klass], border: `1px solid ${GEX_CLASS_COLORS[klass]}55` }}>
              {t(`gex.class.${klass}`)}
            </span>
            <span className="text-[10px] font-mono text-[#8A8A93]">{t("gex.score")}: <span className="text-white font-bold">{data.score}</span></span>
          </div>
          <button
            data-testid="forge-gex-fullscreen-close"
            onClick={(e) => { e.stopPropagation(); setFullscreen(false); }}
            className="text-[#8A8A93] hover:text-white p-1 rounded-sm"
            title={t("gex.close")}
          >
            <X size={18} />
          </button>
        </div>
        <div
          className="flex-1 grid grid-cols-12 gap-4 p-6 overflow-auto"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="col-span-12 lg:col-span-9 flex flex-col gap-3">
            {/* Iter30 — primary surface in fullscreen mirrors the inline panel:
                Strike Path Card on top, Advanced legacy chart collapsible below. */}
            <GEXStrikePathCard
              data={data}
              objective={objective || data.strike_path?.objective}
              onObjectiveChange={setObjective}
              loading={loading}
            />
            <div className="border-t border-[#26262B] pt-3">
              <button
                data-testid="gex-advanced-toggle-fullscreen"
                onClick={() => setShowAdvanced(v => !v)}
                className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#8A8A93] hover:text-white flex items-center gap-1.5"
              >
                {showAdvanced ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                Advanced View · By-Strike Curve & Bars
              </button>
              {showAdvanced && (
                <div className="mt-3 space-y-3">
                  <GEXProfileChart data={data} t={t} height={Math.max(440, window.innerHeight - 360)}
                    manualStrike={manualStrike}
                    onStrikeChange={setManualStrike}
                    onStrikeReset={() => setManualStrike(null)}
                  />
                  <GEXHistorySparkline history={history} t={t} />
                </div>
              )}
            </div>
          </div>
          <div className="col-span-12 lg:col-span-3 flex flex-col gap-3 text-xs font-mono">
            <GEXStats data={data} t={t} />
            <GEXRegimeStability stability={stability} t={t} />
            <GEXControls
              signModel={signModel} setSignModel={setSignModel}
              scaleMode={scaleMode} setScaleMode={setScaleMode}
              t={t}
            />
            <StrategyTracker symbol={symbol} signModel={signModel} t={t} />
            {data.notes?.length > 0 && (
              <div className="bg-[#121216] border border-[#26262B] rounded-sm p-3 space-y-1.5">
                <div className="text-[9px] font-bold uppercase tracking-[0.15em] text-[#8A8A93]">{t("gex.warnings")}</div>
                {data.notes.map((n, i) => (
                  <div key={i} className="text-[11px] text-[#E4E4E7] leading-snug">• {renderNote(t, n)}</div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    )}
    </>
  );
}

function GEXStats({ data, t }) {
  const fmt = (v) => (v == null ? "—" : typeof v === "number" ? v.toLocaleString(undefined, { maximumFractionDigits: 2 }) : v);
  const fmtGex = (v) => {
    if (v == null) return "—";
    const sign = v >= 0 ? "+" : "-";
    const av = Math.abs(v);
    if (av >= 1e6) return `${sign}${(av / 1e6).toFixed(2)}M`;
    if (av >= 1e3) return `${sign}${(av / 1e3).toFixed(0)}K`;
    return `${sign}${av.toFixed(1)}`;
  };
  const regimeLabel = (data.regime === "long_gamma" ? "Long Gamma" :
                      data.regime === "short_gamma" ? "Short Gamma" :
                      data.regime === "transition" ? "Neutral / Transition" : "—");
  const regimeColor = (data.regime === "long_gamma" ? "#00FF88" :
                      data.regime === "short_gamma" ? "#FF3366" :
                      "#FFB800");
  const wallInterp = (k) => {
    const map = {
      RESISTANCE_OR_PINNING_RISK: { txt: "resistance / pinning", color: "#FF3366" },
      POTENTIAL_BREAKOUT_ACCELERATION_IF_BROKEN: { txt: "breakout fuel if broken", color: "#FFB800" },
      SUPPORT_OR_ABSORPTION_RISK: { txt: "support / absorption", color: "#00FF88" },
      FRAGILE_SUPPORT_CASCADE_RISK_IF_BROKEN: { txt: "fragile support / cascade risk", color: "#FFB800" },
      NEUTRAL_FRICTION_TRANSITION: { txt: "neutral friction", color: "#8A8A93" },
      UNKNOWN: { txt: "—", color: "#5F5F66" },
    };
    return map[k] || map.UNKNOWN;
  };
  const callI = wallInterp(data.call_wall_interpretation);
  const putI = wallInterp(data.put_wall_interpretation);

  return (
    <div className="bg-[#121216] border border-[#26262B] rounded-sm p-3 space-y-3">
      {/* Sign-conflict banner — Spec §15.1 */}
      {data.sign_conflict && (
        <div data-testid="gex-sign-conflict" className="bg-[#FF3366]/10 border border-[#FF3366] rounded-sm px-2 py-1.5">
          <div className="text-[9px] font-bold uppercase tracking-[0.15em] text-[#FF3366]">
            ⚠ Sign Conflict
          </div>
          <div className="text-[10px] text-[#E4E4E7] leading-tight mt-1">
            {data.sign_conflict_reason || "Curve and badge disagree — verify Raw/Dealer mode."}
          </div>
        </div>
      )}

      {/* Mode label — Spec §15 */}
      <div className="text-[9px] font-mono text-[#5F5F66] uppercase tracking-[0.15em] border-b border-[#26262B] pb-1.5"
           data-testid="gex-mode-label">
        Mode: <span className="text-white">{data.sign_model === "dealer_short" ? "Dealer GEX" : "Raw GEX"}</span>
        <span className="mx-1.5 text-[#3F3F47]">·</span> Scale: <span className="text-white">{data.scale_mode === "percent_1" ? "1% move" : "Raw $/pt"}</span>
      </div>

      {/* Spot GEX numeric — auditable badge source — Spec §6.3 */}
      <div className="bg-[#08080A] rounded-sm px-2.5 py-2 border border-[#26262B]">
        <div className="text-[9px] uppercase tracking-[0.15em] text-[#8A8A93]">Spot GEX</div>
        <div className="flex items-baseline gap-2">
          <div data-testid="gex-spot-gex" className="text-base font-mono font-bold" style={{ color: regimeColor }}>
            {fmtGex(data.spot_gex)}
          </div>
          <div className="text-[10px] uppercase font-bold tracking-[0.15em]" style={{ color: regimeColor }}>
            → {regimeLabel}
          </div>
        </div>
        {data.neutral_threshold != null && (
          <div className="text-[9px] text-[#5F5F66] font-mono mt-0.5">neutral ±{fmtGex(data.neutral_threshold)}</div>
        )}
      </div>

      <div className="grid grid-cols-2 gap-2">
        <div>
          <div className="text-[9px] uppercase tracking-[0.15em] text-[#8A8A93]">{t("gex.spot")}</div>
          <div className="text-white font-bold" data-testid="gex-spot">${fmt(data.spot)}</div>
        </div>
        <div>
          <div className="text-[9px] uppercase tracking-[0.15em] text-[#8A8A93]">{t("gex.zero_gamma")}</div>
          <div className="text-[#3BB5FF] font-bold" data-testid="gex-zero-gamma">
            {data.zero_gamma ? `$${fmt(data.zero_gamma)}` : "—"}
            {data.spot_distance_to_zero_gamma_pct != null && (
              <span className="text-[9px] text-[#8A8A93] ml-1">
                ({data.spot_distance_to_zero_gamma_pct > 0 ? "+" : ""}{data.spot_distance_to_zero_gamma_pct}%)
              </span>
            )}
          </div>
          {/* Additional flips — Spec §8.3 */}
          {data.zero_gamma_crossings?.length > 1 && (
            <div className="text-[9px] text-[#5F5F66] mt-0.5" data-testid="gex-zero-gamma-flips">
              flips: {data.zero_gamma_crossings.filter(c => c !== data.zero_gamma).map(c => `$${c}`).join(", ")}
            </div>
          )}
        </div>
        <div>
          <div className="text-[9px] uppercase tracking-[0.15em] text-[#8A8A93]">{t("gex.call_wall")}</div>
          <div className="text-[#FF3366] font-bold" data-testid="gex-call-wall">
            {data.call_wall ? `$${fmt(data.call_wall)}` : "—"}
            {data.call_wall_gex != null && (
              <span className="text-[9px] font-mono text-[#8A8A93] ml-1">({fmtGex(data.call_wall_gex)})</span>
            )}
          </div>
          {data.call_wall && (
            <div className="text-[9px] mt-0.5" style={{ color: callI.color }} data-testid="gex-call-wall-interp">
              {callI.txt}
            </div>
          )}
        </div>
        <div>
          <div className="text-[9px] uppercase tracking-[0.15em] text-[#8A8A93]">{t("gex.put_wall")}</div>
          <div className="text-[#00FF88] font-bold" data-testid="gex-put-wall">
            {data.put_wall ? `$${fmt(data.put_wall)}` : "—"}
            {data.put_wall_gex != null && (
              <span className="text-[9px] font-mono text-[#8A8A93] ml-1">({fmtGex(data.put_wall_gex)})</span>
            )}
          </div>
          {data.put_wall && (
            <div className="text-[9px] mt-0.5" style={{ color: putI.color }} data-testid="gex-put-wall-interp">
              {putI.txt}
            </div>
          )}
        </div>
        {data.candidate_strike && (
          <div className="col-span-2">
            <div className="text-[9px] uppercase tracking-[0.15em] text-[#8A8A93]">{t("gex.candidate_strike")}</div>
            <div className="text-[#FFB800] font-bold" data-testid="gex-candidate">
              ${fmt(data.candidate_strike)}
              {data.candidate_strike_gex != null && (
                <span className="text-[9px] font-mono text-[#8A8A93] ml-1">({fmtGex(data.candidate_strike_gex)})</span>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Assigned-Inventory CC Planning block — Spec §13 */}
      {data.cc_planning && (
        <div data-testid="gex-cc-planning" className="bg-[#FFB800]/5 border border-[#FFB800]/40 rounded-sm px-2.5 py-2 space-y-1">
          <div className="text-[9px] font-bold uppercase tracking-[0.15em] text-[#FFB800]">
            Assigned Inventory — CC Planning
          </div>
          <div className="text-[10px] font-mono text-[#E4E4E7] grid grid-cols-2 gap-x-2 gap-y-0.5">
            <div>Original CSP:</div><div className="text-white">${data.cc_planning.original_csp_strike}</div>
            <div>Premium Bank:</div><div className="text-white">${data.cc_planning.premium_bank}</div>
            <div>Candidate CC:</div><div className="text-white">${data.cc_planning.candidate_cc_strike}</div>
            <div>P/L Cycle:</div>
            <div className={data.cc_planning.pl_cycle >= data.cc_planning.required_pl ? "text-[#00FF88]" : "text-[#FF3366]"}>
              ${data.cc_planning.pl_cycle}
            </div>
            <div>ROI Cycle:</div>
            <div className={data.cc_planning.roi_cycle_pct >= 3 ? "text-[#00FF88]" : "text-[#FF3366]"}>
              {data.cc_planning.roi_cycle_pct.toFixed(2)}%
            </div>
            <div>Required Pc:</div><div className="text-white">${data.cc_planning.required_cc_premium}</div>
          </div>
        </div>
      )}
    </div>
  );
}

function GEXControls({ signModel, setSignModel, scaleMode, setScaleMode, t }) {
  return (
    <div className="bg-[#121216] border border-[#26262B] rounded-sm p-3 space-y-2">
      <div>
        <div className="text-[9px] uppercase tracking-[0.15em] text-[#8A8A93] mb-1">{t("gex.sign_model")}</div>
        <div className="flex gap-1">
          {["dealer_short", "raw"].map(v => (
            <button key={v} data-testid={`gex-sign-${v}`} onClick={() => setSignModel(v)}
              className={`flex-1 text-[10px] font-mono px-2 py-1 rounded-sm border ${signModel === v ? "border-[#00FF88] text-[#00FF88] bg-[#00FF88]/10" : "border-[#26262B] text-[#8A8A93] hover:text-white"}`}
            >{t(`gex.sign.${v}`)}</button>
          ))}
        </div>
      </div>
      <div>
        <div className="text-[9px] uppercase tracking-[0.15em] text-[#8A8A93] mb-1">{t("gex.scale_mode")}</div>
        <div className="flex gap-1">
          {["raw", "percent_1"].map(v => (
            <button key={v} data-testid={`gex-scale-${v}`} onClick={() => setScaleMode(v)}
              className={`flex-1 text-[10px] font-mono px-2 py-1 rounded-sm border ${scaleMode === v ? "border-[#00FF88] text-[#00FF88] bg-[#00FF88]/10" : "border-[#26262B] text-[#8A8A93] hover:text-white"}`}
            >{t(`gex.scale.${v}`)}</button>
          ))}
        </div>
      </div>
    </div>
  );
}

// Modern horizontal gamma profile chart with the Net GEX vs Price curve
// overlaid (same coordinate system — price on Y, net_gex on X). Bars are
// rendered with gradient fills (dark-to-bright) for a clean Optionar-style
// look; a cyan cumulative-curve traces the continuous net dealer gamma
// across the price grid. `fill` lets the chart expand to its parent
// container (used by the fullscreen overlay).
function GEXProfileChart({ data, t, height, fill, manualStrike, onStrikeChange, onStrikeReset }) {
  const strikes = data.strikes || [];
  const curve = data.gex_curve || [];
  const chartH = height || 460;
  const uid = useMemo(() => `gex-${Math.random().toString(36).slice(2, 8)}`, []);
  const svgRef = useRef(null);
  const debounceRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const [hoverPrice, setHoverPrice] = useState(null);
  // Iter28 — hover tooltip on curve. Tracks the cursor's interpolated price
  // and the curve's GEX value at that price so the user can read off any
  // point of the Net-GEX-vs-Price line.
  const [hover, setHover] = useState(null);  // {price, net_gex, x, y}

  if (!strikes.length) return (
    <div className="text-xs text-[#8A8A93] font-mono text-center py-10 border border-[#26262B] rounded-sm bg-[#121216]">
      {t("gex.empty")}
    </div>
  );

  const W = 900;
  const H = chartH;
  const padL = 70, padR = 90, padT = 22, padB = 30;
  const plotW = W - padL - padR, plotH = H - padT - padB;

  // Compute displayStrike BEFORE priceMin/Max so we can widen the price
  // domain to include it (out-of-range overrides should remain visible).
  const draggable = typeof onStrikeChange === "function";
  const strikeValues = strikes.map(s => s.strike).sort((a, b) => a - b);
  const fallbackStrike = strikeValues.length ? (() => {
    const anchor = data.spot != null ? data.spot : strikeValues[Math.floor(strikeValues.length / 2)];
    let best = strikeValues[0], bestD = Infinity;
    for (const k of strikeValues) {
      const d = Math.abs(k - anchor);
      if (d < bestD) { best = k; bestD = d; }
    }
    return best;
  })() : null;
  const displayStrike = manualStrike != null
    ? manualStrike
    : (data.candidate_strike != null ? data.candidate_strike : fallbackStrike);

  // Shared price domain so bars AND curve line up (union of strikes + curve
  // prices + displayStrike). Including displayStrike guarantees the yellow
  // candidate line and its drag handle are always in view.
  const priceMin = Math.min(
    strikes[0].strike,
    ...(curve.length ? [curve[0].price] : []),
    ...(displayStrike != null ? [displayStrike] : []),
  );
  const priceMax = Math.max(
    strikes[strikes.length - 1].strike,
    ...(curve.length ? [curve[curve.length - 1].price] : []),
    ...(displayStrike != null ? [displayStrike] : []),
  );
  const yForPrice = (k) => padT + (1 - (k - priceMin) / Math.max(priceMax - priceMin, 1e-6)) * plotH;

  // Shared X-axis scale: max absolute value across bars AND curve, so both
  // use the same zero-centered axis.
  const maxAbs = Math.max(
    1,
    ...strikes.map(s => Math.abs(s.net_gex)),
    ...curve.map(c => Math.abs(c.net_gex)),
  );
  const zeroX = padL + plotW / 2;
  const xForGex = (g) => zeroX + (g / maxAbs) * (plotW / 2);
  const barH = Math.max(3, (plotH / Math.max(strikes.length, 1)) * 0.78);

  const fmtMoney = (v) => {
    const abs = Math.abs(v);
    if (abs >= 1e9) return `${(v / 1e9).toFixed(1)}B`;
    if (abs >= 1e6) return `${(v / 1e6).toFixed(1)}M`;
    if (abs >= 1e3) return `${(v / 1e3).toFixed(0)}K`;
    return v.toFixed(0);
  };

  // --- Draggable candidate-strike slider ------------------------------------
  // Plain (non-memoised) helpers — correctness matters, not identity.
  // Hook rules forbid useMemo/useCallback here because of the earlier
  // `if (!strikes.length) return ...` early return above.
  const snapToStrike = (raw) => {
    if (!strikeValues.length) return raw;
    let best = strikeValues[0], bestD = Infinity;
    for (const k of strikeValues) {
      const d = Math.abs(k - raw);
      if (d < bestD) { best = k; bestD = d; }
    }
    return best;
  };
  // Debounce the parent callback so rapid drag doesn't spam /api/forge/gex.
  const emitStrike = (k) => {
    if (!draggable) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => { onStrikeChange(k); }, 250);
  };
  const priceFromClientY = (clientY) => {
    const svg = svgRef.current;
    if (!svg) return null;
    const rect = svg.getBoundingClientRect();
    // Map clientY → SVG viewBox Y (since we use viewBox, scaling is linear).
    const svgY = ((clientY - rect.top) / rect.height) * H;
    const clamped = Math.min(Math.max(svgY, padT), padT + plotH);
    const frac = 1 - (clamped - padT) / plotH;
    return priceMin + frac * (priceMax - priceMin);
  };
  const handlePointerDown = (e) => {
    if (!draggable) return;
    e.preventDefault();
    setDragging(true);
    const raw = priceFromClientY(e.clientY);
    if (raw != null) {
      const snapped = snapToStrike(raw);
      setHoverPrice(snapped);
      emitStrike(snapped);
    }
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };
  const handlePointerMove = (e) => {
    const raw = priceFromClientY(e.clientY);
    if (raw == null) return;
    if (dragging) {
      const snapped = snapToStrike(raw);
      setHoverPrice(snapped);
      emitStrike(snapped);
      return;
    }
    // Iter28 — non-drag hover: interpolate the curve at the cursor's price
    // and surface the GEX value through a small tooltip near the cursor.
    if (curve.length >= 2) {
      // Clamp price to curve domain
      const pmin = curve[0].price, pmax = curve[curve.length - 1].price;
      const price = Math.max(pmin, Math.min(pmax, raw));
      // Find bracketing pair (curve is sorted ascending by price)
      let lo = 0, hi = curve.length - 1;
      for (let i = 1; i < curve.length; i++) {
        if (curve[i].price >= price) { lo = i - 1; hi = i; break; }
      }
      const a = curve[lo], b = curve[hi];
      const t = b.price === a.price ? 0 : (price - a.price) / (b.price - a.price);
      const net_gex = a.net_gex + t * (b.net_gex - a.net_gex);
      setHover({ price, net_gex, x: xForGex(net_gex), y: yForPrice(price) });
    }
  };
  const handlePointerLeave = () => { setHover(null); };
  const handlePointerUp = (e) => {
    if (!dragging) return;
    setDragging(false);
    setHoverPrice(null);
    e.currentTarget.releasePointerCapture?.(e.pointerId);
  };

  // Build the Net GEX vs Price curve path
  const curvePath = curve.length > 1
    ? curve.map((p, i) => `${i === 0 ? "M" : "L"}${xForGex(p.net_gex).toFixed(2)},${yForPrice(p.price).toFixed(2)}`).join(" ")
    : "";
  // Filled area under the curve (to zero line) — subtle green/red shading
  const curveArea = curve.length > 1
    ? `M${xForGex(curve[0].net_gex).toFixed(2)},${yForPrice(curve[0].price).toFixed(2)} ` +
      curve.slice(1).map(p => `L${xForGex(p.net_gex).toFixed(2)},${yForPrice(p.price).toFixed(2)}`).join(" ") +
      ` L${zeroX.toFixed(2)},${yForPrice(curve[curve.length - 1].price).toFixed(2)}` +
      ` L${zeroX.toFixed(2)},${yForPrice(curve[0].price).toFixed(2)} Z`
    : "";

  // Y-axis tick prices (8 ticks)
  const yTicks = 8;
  const tickPrices = Array.from({ length: yTicks + 1 }, (_, i) => priceMin + (priceMax - priceMin) * (i / yTicks));

  // X-axis tick values (5 ticks, symmetric around zero)
  const xTickStep = maxAbs / 2;
  const xTicks = [-maxAbs, -xTickStep, 0, xTickStep, maxAbs];

  const containerCls = fill
    ? "bg-[#0A0A0D] border border-[#26262B] rounded-sm p-2 h-full"
    : "bg-[#0A0A0D] border border-[#26262B] rounded-sm p-2";
  const svgCls = fill ? "w-full h-full" : "w-full h-auto";

  return (
    <div data-testid="gex-profile-chart" className={containerCls}>
      {/* Reset-override badge (shown only when user has manually dragged the strike) */}
      {manualStrike != null && typeof onStrikeReset === "function" && (
        <div className="flex items-center justify-end px-1 pb-1">
          <button
            data-testid="gex-strike-reset"
            onClick={onStrikeReset}
            className="text-[10px] font-mono px-2 py-0.5 rounded-sm border border-[#FFB800]/40 text-[#FFB800] bg-[#FFB800]/5 hover:bg-[#FFB800]/10 transition-colors"
            title={t("gex.strike.reset_tooltip")}
          >
            {t("gex.strike.reset")} · K ${Number(manualStrike).toFixed(2)}
          </button>
        </div>
      )}
      <svg
        ref={svgRef}
        viewBox={`0 0 ${W} ${H}`}
        className={svgCls}
        preserveAspectRatio={fill ? "xMidYMid meet" : "none"}
        onPointerMove={handlePointerMove}
        onPointerLeave={handlePointerLeave}
      >
        <defs>
          <linearGradient id={`${uid}-grad-pos`} x1="0" x2="1" y1="0" y2="0">
            <stop offset="0%" stopColor="#00FF88" stopOpacity="0.25" />
            <stop offset="100%" stopColor="#00FF88" stopOpacity="0.95" />
          </linearGradient>
          <linearGradient id={`${uid}-grad-neg`} x1="1" x2="0" y1="0" y2="0">
            <stop offset="0%" stopColor="#FF3366" stopOpacity="0.25" />
            <stop offset="100%" stopColor="#FF3366" stopOpacity="0.95" />
          </linearGradient>
          <linearGradient id={`${uid}-curve-fill`} x1="0" x2="1" y1="0" y2="0">
            <stop offset="0%" stopColor="#FF3366" stopOpacity="0.08" />
            <stop offset="50%" stopColor="#3BB5FF" stopOpacity="0" />
            <stop offset="100%" stopColor="#00FF88" stopOpacity="0.08" />
          </linearGradient>
          <filter id={`${uid}-glow`} x="-20%" y="-20%" width="140%" height="140%">
            <feGaussianBlur stdDeviation="2.2" result="blur" />
            <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
          </filter>
        </defs>

        {/* Plot background */}
        <rect x={padL} y={padT} width={plotW} height={plotH} fill="#08080A" stroke="#1A1A1F" strokeWidth="1" />

        {/* Y grid */}
        {tickPrices.map((p, i) => (
          <g key={`yg-${i}`}>
            <line x1={padL} x2={W - padR} y1={yForPrice(p)} y2={yForPrice(p)} stroke="#15151A" strokeWidth="1" />
            <text x={padL - 8} y={yForPrice(p) + 3} fontSize="10" fill="#6F6F78" textAnchor="end" fontFamily="monospace">
              ${p.toFixed(p < 20 ? 2 : 1)}
            </text>
          </g>
        ))}

        {/* X grid (subtle) */}
        {xTicks.map((g, i) => (
          <g key={`xg-${i}`}>
            <line x1={xForGex(g)} x2={xForGex(g)} y1={padT} y2={H - padB} stroke={g === 0 ? "#3A3A44" : "#15151A"} strokeWidth={g === 0 ? 1 : 1} strokeDasharray={g === 0 ? "" : "2 3"} />
            <text x={xForGex(g)} y={H - padB + 14} fontSize="9" fill="#6F6F78" textAnchor="middle" fontFamily="monospace">
              {g === 0 ? "0" : fmtMoney(g)}
            </text>
          </g>
        ))}

        {/* Curve filled area */}
        {curveArea && (
          <path d={curveArea} fill={`url(#${uid}-curve-fill)`} />
        )}

        {/* Bars (gradient-filled) */}
        {strikes.map((s) => {
          const y = yForPrice(s.strike) - barH / 2;
          const x = s.net_gex >= 0 ? zeroX : xForGex(s.net_gex);
          const w = Math.max(1, Math.abs(xForGex(s.net_gex) - zeroX));
          const grad = s.net_gex >= 0 ? `url(#${uid}-grad-pos)` : `url(#${uid}-grad-neg)`;
          return (
            <rect key={`bar-${s.strike}`} x={x} y={y} width={w} height={barH} fill={grad} rx={1.5} data-testid={`gex-bar-${s.strike}`}>
              <title>Strike ${s.strike} · net GEX {fmtMoney(s.net_gex)} · OI {s.total_oi.toLocaleString()}</title>
            </rect>
          );
        })}

        {/* Net GEX vs Price curve */}
        {curvePath && (
          <path
            data-testid="gex-curve-line"
            d={curvePath}
            fill="none"
            stroke="#3BB5FF"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            filter={`url(#${uid}-glow)`}
            opacity="0.95"
          />
        )}

        {/* Spot reference (white dashed) */}
        {data.spot != null && (
          <g>
            <line x1={padL} x2={W - padR} y1={yForPrice(data.spot)} y2={yForPrice(data.spot)} stroke="#FFFFFF" strokeWidth="1.4" strokeDasharray="4 3" />
            <text x={W - padR + 4} y={yForPrice(data.spot) + 3} fontSize="10" fill="#FFFFFF" fontFamily="monospace">
              {t("gex.spot")} ${data.spot}
            </text>
          </g>
        )}

        {/* Zero-gamma (blue solid) */}
        {data.zero_gamma != null && data.zero_gamma >= priceMin && data.zero_gamma <= priceMax && (
          <g>
            <line x1={padL} x2={W - padR} y1={yForPrice(data.zero_gamma)} y2={yForPrice(data.zero_gamma)} stroke="#3BB5FF" strokeWidth="1.4" />
            <text x={W - padR + 4} y={yForPrice(data.zero_gamma) + 3} fontSize="10" fill="#3BB5FF" fontFamily="monospace">
              γ₀ ${data.zero_gamma}
            </text>
          </g>
        )}

        {/* Candidate strike (yellow dashed) — draggable when onStrikeChange provided */}
        {displayStrike != null && displayStrike >= priceMin && displayStrike <= priceMax && (
          <g data-testid="gex-candidate-strike-group">
            <line x1={padL} x2={W - padR} y1={yForPrice(displayStrike)} y2={yForPrice(displayStrike)}
              stroke="#FFB800" strokeWidth={dragging ? 2 : 1.4} strokeDasharray="5 2" />
            <text x={W - padR + 4} y={yForPrice(displayStrike) + 3} fontSize="10" fill="#FFB800" fontFamily="monospace">
              K ${Number(displayStrike).toFixed(2)}{manualStrike != null ? " ◆" : ""}
            </text>
            {/* Drag handle at the left edge of the candidate line */}
            {draggable && (
              <g
                data-testid="gex-candidate-handle"
                style={{ cursor: "ns-resize", touchAction: "none" }}
                onPointerDown={handlePointerDown}
                onPointerMove={handlePointerMove}
                onPointerUp={handlePointerUp}
                onPointerCancel={handlePointerUp}
              >
                {/* Invisible wider hit-area so the handle is easy to grab */}
                <rect x={padL - 2} y={yForPrice(displayStrike) - 10} width={plotW + 4} height={20}
                  fill="transparent" />
                {/* Visible handle knob */}
                <circle cx={padL + 10} cy={yForPrice(displayStrike)} r={dragging ? 7 : 5.5}
                  fill="#FFB800" stroke="#08080A" strokeWidth="1.5" />
                <path d={`M${padL + 6},${yForPrice(displayStrike) - 2} L${padL + 14},${yForPrice(displayStrike) - 2} M${padL + 6},${yForPrice(displayStrike) + 2} L${padL + 14},${yForPrice(displayStrike) + 2}`}
                  stroke="#08080A" strokeWidth="1.1" strokeLinecap="round" />
              </g>
            )}
          </g>
        )}

        {/* Drag ghost line — shows where the strike will snap while dragging */}
        {draggable && dragging && hoverPrice != null && hoverPrice !== displayStrike && (
          <g pointerEvents="none">
            <line x1={padL} x2={W - padR} y1={yForPrice(hoverPrice)} y2={yForPrice(hoverPrice)}
              stroke="#FFB800" strokeWidth="1" strokeDasharray="2 2" opacity="0.5" />
            <text x={W - padR + 4} y={yForPrice(hoverPrice) + 3} fontSize="10" fill="#FFB800"
              fontFamily="monospace" opacity="0.8">${Number(hoverPrice).toFixed(2)}</text>
          </g>
        )}

        {/* Iter28 — hover tooltip on curve. Shows the interpolated GEX value
             at the cursor's price; only renders when not dragging so the
             tooltip never fights the drag handle. */}
        {hover && !dragging && (
          <g data-testid="gex-curve-tooltip" pointerEvents="none">
            <line x1={padL} x2={W - padR} y1={hover.y} y2={hover.y}
                  stroke="#3BB5FF" strokeWidth="1" strokeDasharray="2 2" opacity="0.55" />
            <circle cx={hover.x} cy={hover.y} r={4} fill="#3BB5FF" stroke="#08080A" strokeWidth="1.5" />
            {(() => {
              const tipW = 122, tipH = 38;
              // Anchor the tooltip to the LEFT or RIGHT of the cursor based
              // on which side of zero we're on, so it never clips the edge.
              const onRight = hover.x < zeroX;
              const tx = onRight ? Math.min(W - padR - tipW, hover.x + 12) : Math.max(padL, hover.x - tipW - 12);
              const ty = Math.min(padT + plotH - tipH, Math.max(padT, hover.y - tipH / 2));
              return (
                <g transform={`translate(${tx}, ${ty})`}>
                  <rect x={0} y={0} width={tipW} height={tipH} rx={3} fill="#0A0A0D" stroke="#3BB5FF" strokeOpacity="0.6" />
                  <text x={8} y={14} fontSize="10" fontFamily="monospace" fill="#8A8A93">price</text>
                  <text x={tipW - 8} y={14} fontSize="10" fontFamily="monospace" fill="#FFFFFF" textAnchor="end">${hover.price.toFixed(2)}</text>
                  <text x={8} y={30} fontSize="10" fontFamily="monospace" fill="#8A8A93">GEX</text>
                  <text x={tipW - 8} y={30} fontSize="10" fontFamily="monospace"
                        fill={hover.net_gex >= 0 ? "#00FF88" : "#FF3366"} textAnchor="end" fontWeight="bold">
                    {hover.net_gex >= 0 ? "+" : ""}{fmtMoney(hover.net_gex)}
                  </text>
                </g>
              );
            })()}
          </g>
        )}

        {/* Axis legends */}
        <text x={padL} y={H - 6} fontSize="10" fill="#5F5F66" fontFamily="monospace">← {t("gex.axis.put_compression")}</text>
        <text x={W - padR} y={H - 6} fontSize="10" fill="#5F5F66" fontFamily="monospace" textAnchor="end">{t("gex.axis.call_compression")} →</text>

        {/* Curve legend */}
        <g transform={`translate(${padL + 8}, ${padT + 10})`}>
          <rect x={-4} y={-10} width={138} height={18} fill="#0A0A0D" fillOpacity="0.75" rx={2} />
          <line x1={0} x2={18} y1={0} y2={0} stroke="#3BB5FF" strokeWidth="2" />
          <text x={24} y={3} fontSize="10" fill="#E4E4E7" fontFamily="monospace">{t("gex.curve_legend")}</text>
        </g>
      </svg>
    </div>
  );
}

// Phase 6 — tiny sparkline of zero-gamma drift vs spot over the last 7 days.
function GEXHistorySparkline({ history, t }) {
  if (!history) return null;
  const rows = (history.rows || []).filter(r => r.spot != null);
  if (rows.length < 2) {
    return (
      <div data-testid="gex-history" className="text-[10px] font-mono text-[#5F5F66] text-center py-2 border border-dashed border-[#26262B] rounded-sm">
        {t("gex.history.empty")}
      </div>
    );
  }
  const W = 900, H = 100, padL = 56, padR = 70, padT = 12, padB = 18;
  const plotW = W - padL - padR, plotH = H - padT - padB;
  const allValues = rows.flatMap(r => [r.spot, r.zero_gamma].filter(v => v != null));
  const minY = Math.min(...allValues);
  const maxY = Math.max(...allValues);
  const xForI = (i) => padL + (i / Math.max(rows.length - 1, 1)) * plotW;
  const yForV = (v) => padT + (1 - (v - minY) / Math.max(maxY - minY, 1e-6)) * plotH;
  const linePath = (key) => rows.map((r, i) => r[key] != null ? `${i === 0 ? "M" : "L"}${xForI(i).toFixed(1)},${yForV(r[key]).toFixed(1)}` : null).filter(Boolean).join(" ");
  const last = rows[rows.length - 1];
  return (
    <div data-testid="gex-history" className="bg-[#0A0A0D] border border-[#26262B] rounded-sm p-1.5">
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto" preserveAspectRatio="none">
        <rect x={padL} y={padT} width={plotW} height={plotH} fill="#08080A" stroke="#15151A" />
        <text x={8} y={padT + 10} fontSize="10" fill="#8A8A93" fontFamily="monospace">{t("gex.history")} · {history.days}d</text>
        <path d={linePath("spot")} fill="none" stroke="#FFFFFF" strokeWidth="1.6" />
        <path d={linePath("zero_gamma")} fill="none" stroke="#3BB5FF" strokeWidth="1.6" />
        {last?.spot != null && <text x={W - padR + 4} y={yForV(last.spot) + 3} fontSize="10" fill="#FFFFFF" fontFamily="monospace">${last.spot}</text>}
        {last?.zero_gamma != null && <text x={W - padR + 4} y={yForV(last.zero_gamma) + 3} fontSize="10" fill="#3BB5FF" fontFamily="monospace">${last.zero_gamma}</text>}
      </svg>
    </div>
  );
}


// Iter28 — Regime Stability sparkline. Plots the spot_gex sign trace over
// the last N snapshots with regime-shaded bands so the user can see WHEN
// the gamma side flipped. Surfaces dominant regime + stability % + last
// transition timestamp as an early-warning ahead of new CSP entries.
function GEXRegimeStability({ stability, t }) {
  if (!stability || (stability.snapshot_count || 0) < 2) {
    return (
      <div data-testid="gex-regime-stability-empty" className="bg-[#121216] border border-[#26262B] rounded-sm p-2.5">
        <div className="text-[9px] font-bold uppercase tracking-[0.15em] text-[#8A8A93]">Regime Stability</div>
        <div className="text-[10px] font-mono text-[#5F5F66] py-2">Collecting snapshots… check back after a few hits.</div>
      </div>
    );
  }
  const trace = (stability.trace || []).filter(p => p.spot_gex != null);
  const W = 240, H = 56, padL = 4, padR = 4, padT = 4, padB = 4;
  const plotW = W - padL - padR, plotH = H - padT - padB;
  const maxAbs = Math.max(1, ...trace.map(p => Math.abs(p.spot_gex || 0)));
  const yMid = padT + plotH / 2;
  const xForI = (i) => padL + (i / Math.max(trace.length - 1, 1)) * plotW;
  const yForV = (v) => yMid - (v / maxAbs) * (plotH / 2);
  const polyPoints = trace.map((p, i) => `${xForI(i).toFixed(1)},${yForV(p.spot_gex).toFixed(1)}`).join(" ");
  const fmtAgo = (iso) => {
    if (!iso) return "—";
    const ms = Date.now() - new Date(iso).getTime();
    const d = ms / 86400000;
    if (d < 1) return `${(ms / 3600000).toFixed(0)}h ago`;
    if (d < 30) return `${d.toFixed(0)}d ago`;
    return `${(d / 30).toFixed(0)}mo ago`;
  };
  const dominantColor = stability.dominant_regime === "long_gamma" ? "#00FF88"
                      : stability.dominant_regime === "short_gamma" ? "#FF3366"
                      : "#FFB800";
  const lastT = stability.last_transition;
  return (
    <div data-testid="gex-regime-stability" className="bg-[#121216] border border-[#26262B] rounded-sm p-2.5 space-y-2">
      <div className="flex items-center justify-between">
        <div className="text-[9px] font-bold uppercase tracking-[0.15em] text-[#8A8A93]">Regime Stability · {stability.days}d</div>
        <div className="text-[9px] font-mono text-[#5F5F66]">{stability.snapshot_count} snaps</div>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto" preserveAspectRatio="none" data-testid="gex-regime-stability-chart">
        {/* Long-gamma upper band shading */}
        <rect x={padL} y={padT} width={plotW} height={plotH / 2} fill="#00FF88" fillOpacity="0.05" />
        <rect x={padL} y={yMid} width={plotW} height={plotH / 2} fill="#FF3366" fillOpacity="0.05" />
        {/* Zero line */}
        <line x1={padL} x2={W - padR} y1={yMid} y2={yMid} stroke="#3F3F47" strokeWidth="0.7" strokeDasharray="2 2" />
        {/* Spot GEX trace */}
        {trace.length >= 2 && (
          <polyline points={polyPoints} fill="none" stroke="#3BB5FF" strokeWidth="1.4" strokeLinejoin="round" strokeLinecap="round" />
        )}
        {/* Marker for the last transition */}
        {lastT && (() => {
          const idx = trace.findIndex(p => p.at === lastT.at);
          if (idx < 0) return null;
          return (
            <line x1={xForI(idx)} x2={xForI(idx)} y1={padT} y2={padT + plotH}
                  stroke="#FFB800" strokeWidth="1" strokeDasharray="2 2" opacity="0.7" />
          );
        })()}
      </svg>
      <div className="grid grid-cols-3 gap-2 text-[10px] font-mono">
        <div>
          <div className="text-[9px] uppercase tracking-[0.15em] text-[#8A8A93]">Dominant</div>
          <div data-testid="gex-stability-dominant" className="font-bold" style={{ color: dominantColor }}>
            {stability.dominant_regime === "long_gamma" ? "Long γ"
              : stability.dominant_regime === "short_gamma" ? "Short γ"
              : stability.dominant_regime === "transition" ? "Transition" : "—"}
          </div>
        </div>
        <div>
          <div className="text-[9px] uppercase tracking-[0.15em] text-[#8A8A93]">Stability</div>
          <div data-testid="gex-stability-pct" className="text-white font-bold">{stability.stability_pct}%</div>
        </div>
        <div>
          <div className="text-[9px] uppercase tracking-[0.15em] text-[#8A8A93]">Last flip</div>
          <div data-testid="gex-stability-last-flip" className="text-[#FFB800] font-bold">{lastT ? fmtAgo(lastT.at) : "none"}</div>
        </div>
      </div>
      {lastT && (
        <div className="text-[10px] text-[#E4E4E7] leading-snug border-t border-[#26262B] pt-1.5">
          {fmtAgo(lastT.at)}: <span className="font-bold" style={{ color: lastT.from === "long_gamma" ? "#00FF88" : "#FF3366" }}>
            {lastT.from === "long_gamma" ? "Long γ" : "Short γ"}
          </span> → <span className="font-bold" style={{ color: lastT.to === "long_gamma" ? "#00FF88" : "#FF3366" }}>
            {lastT.to === "long_gamma" ? "Long γ" : "Short γ"}
          </span> {" "}
          @ ${lastT.spot}
        </div>
      )}
    </div>
  );
}
